import React from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../components/Input';
import Dropdown from '../../../../components/Dropdown';
import TextArea from '../../../../components/TextArea';
import Button from '../../../../components/Button';
import { VALIDATION_FIELD_MAPPING } from '../../../../utils/appConstant';
import { Validation } from '../../../../interfaces/common';
import { VisitFormData, ActiveGuest } from '../../../../interfaces/property-visit.interface';
import GuestAlertIcon from './GuestAlertIcon';

interface VisitTabProps {
  formData: VisitFormData;
  validation: Validation;
  isEditable: boolean;
  isAdmin: boolean;
  isInspector: boolean;
  internalNoteRequired: boolean;
  isAddMode: boolean;
  dateMinDate?: string;
  dateMaxDate?: string;
  allProperties: any[];
  properties: any[];
  allVisitTypes: any[];
  visitTypes: any[];
  allUserRoles: any[];
  userRoles: any[];
  allVisitedByOptions: any[];
  visitedByOptions: any[];
  allVisitStatuses: any[];
  dropdownSearchData: any;
  activeGuest?: ActiveGuest | null;
  loading: boolean;
  onInputChange: (e: any, name: string) => void;
  onInputBlur: (e: any, name: string) => void;
  onDropdownSearchChange: (e: any, name: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

const VisitTab: React.FC<VisitTabProps> = ({
  formData, validation, isEditable, isAdmin, isInspector, isAddMode, internalNoteRequired, dateMinDate, dateMaxDate,
  allProperties, properties, allVisitTypes, visitTypes,
  allUserRoles, userRoles, allVisitedByOptions, visitedByOptions, allVisitStatuses,
  dropdownSearchData, activeGuest, loading,
  onInputChange, onInputBlur, onDropdownSearchChange, onSave, onCancel,
}) => {
  const { t } = useTranslation();

  const fieldError = (name: string) => ({
    error: !!validation.fields.find((f) => f.field === name),
    errorMessage: validation.fields.find((f) => f.field === name)?.message ?? '',
  });

  const statusOptions = (allVisitStatuses || []).map((s: any) => ({ label: s.name, value: s.id }));

  return (
    <>
      <form className="card-body" autoComplete="off">
        <div className="row g-6">
          <h6 className="mb-0">
            1. {t('propertyVisits.addEditView.pageTexts.form.sections.primaryDetails')}
          </h6>

          {/* Property */}
          <div className="col-md-4">
            <div className="form-floating form-floating-outline">
              <Dropdown
                values={formData.propertyId ? [formData.propertyId] : []}
                required
                disabled={!isEditable || !isAddMode}
                options={(properties || []).map((p: any) => ({ label: p.name, value: p.id }))}
                allOptions={(allProperties || []).map((p: any) => ({ label: p.name, value: p.id }))}
                id={VALIDATION_FIELD_MAPPING.propertyId}
                name={VALIDATION_FIELD_MAPPING.propertyId}
                searchText={dropdownSearchData.propertyId}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.addEditView.pageTexts.form.property.placeholder')}
                label={t('propertyVisits.addEditView.pageTexts.form.property.label')}
                {...fieldError(VALIDATION_FIELD_MAPPING.propertyId)}
                onChange={onInputChange}
                onBlur={onInputBlur}
              />
            </div>
          </div>

          {/* Visit Date */}
          <div className="col-md-4">
            <div className="d-flex align-items-center gap-2">
              <div className="form-floating form-floating-outline flex-fill">
                <Input
                  type="date"
                  required
                  id={VALIDATION_FIELD_MAPPING.visitDate}
                  name={VALIDATION_FIELD_MAPPING.visitDate}
                  label={t('propertyVisits.addEditView.pageTexts.form.visitDate.label')}
                  placeholder={t('propertyVisits.addEditView.pageTexts.form.visitDate.label')}
                  value={formData.visitDate}
                  disabled={!isEditable}
                  min={dateMinDate}
                  max={dateMaxDate}
                  {...fieldError(VALIDATION_FIELD_MAPPING.visitDate)}
                  onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.visitDate)}
                  onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.visitDate)}
                />
              </div>
              {activeGuest && <GuestAlertIcon guest={activeGuest} />}
            </div>
          </div>

          {/* Visit Type */}
          <div className="col-md-4">
            <div className="form-floating form-floating-outline">
              <Dropdown
                values={formData.visitType ? [formData.visitType] : []}
                required
                disabled={!isEditable}
                options={(visitTypes || []).map((v: any) => ({ label: v.name, value: v.id }))}
                allOptions={(allVisitTypes || []).map((v: any) => ({ label: v.name, value: v.id }))}
                id={VALIDATION_FIELD_MAPPING.visitType}
                name={VALIDATION_FIELD_MAPPING.visitType}
                searchText={dropdownSearchData.visitType}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.addEditView.pageTexts.form.visitType.placeholder')}
                label={t('propertyVisits.addEditView.pageTexts.form.visitType.label')}
                {...fieldError(VALIDATION_FIELD_MAPPING.visitType)}
                onChange={onInputChange}
                onBlur={onInputBlur}
              />
            </div>
          </div>

          {/* User Role */}
          <div className="col-md-4">
            <div className="form-floating form-floating-outline">
              <Dropdown
                values={formData.userRole ? [formData.userRole] : []}
                required
                disabled={!isEditable || isInspector}
                options={(userRoles || []).map((r: any) => ({ label: r.name, value: r.id }))}
                allOptions={(allUserRoles || []).map((r: any) => ({ label: r.name, value: r.id }))}
                id={VALIDATION_FIELD_MAPPING.userRole}
                name={VALIDATION_FIELD_MAPPING.userRole}
                searchText={dropdownSearchData.userRole}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.addEditView.pageTexts.form.userRole.placeholder')}
                label={t('propertyVisits.addEditView.pageTexts.form.userRole.label')}
                {...fieldError(VALIDATION_FIELD_MAPPING.userRole)}
                onChange={onInputChange}
                onBlur={onInputBlur}
              />
            </div>
          </div>

          {/* Visited By */}
          <div className="col-md-4">
            <div className="form-floating form-floating-outline">
              <Dropdown
                values={formData.visitedBy ? [formData.visitedBy] : []}
                required
                disabled={!isEditable || isInspector || !formData.userRole}
                options={(visitedByOptions || []).map((u: any) => ({ label: u.name, value: u.id }))}
                allOptions={(allVisitedByOptions || []).map((u: any) => ({ label: u.name, value: u.id }))}
                id={VALIDATION_FIELD_MAPPING.visitedBy}
                name={VALIDATION_FIELD_MAPPING.visitedBy}
                searchText={dropdownSearchData.visitedBy}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.addEditView.pageTexts.form.visitedBy.placeholder')}
                label={t('propertyVisits.addEditView.pageTexts.form.visitedBy.label')}
                {...fieldError(VALIDATION_FIELD_MAPPING.visitedBy)}
                onChange={onInputChange}
                onBlur={onInputBlur}
              />
            </div>
          </div>

          {/* Visit Note */}
          <div className="col-12">
            <div className="form-floating form-floating-outline">
              <TextArea
                id={VALIDATION_FIELD_MAPPING.visitNote}
                name={VALIDATION_FIELD_MAPPING.visitNote}
                label={t('propertyVisits.addEditView.pageTexts.form.visitNote.label')}
                placeholder={t('propertyVisits.addEditView.pageTexts.form.visitNote.placeholder')}
                value={formData.visitNote}
                rows={4}
                className="form-control-lg h-100"
                disabled={!isEditable}
                onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.visitNote)}
              />
            </div>
          </div>

        </div>

        <hr className="my-6 mx-n4" />

        <div className="row g-6">
          <h6 className="mb-0">
            2. {t('propertyVisits.addEditView.pageTexts.form.sections.notesForCoHost')}
          </h6>

          {/* Internal Note */}
          <div className="col-md-9">
            <div className="form-floating form-floating-outline">
              <TextArea
                id={VALIDATION_FIELD_MAPPING.internalNote}
                name={VALIDATION_FIELD_MAPPING.internalNote}
                required={internalNoteRequired}
                label={t('propertyVisits.addEditView.pageTexts.form.internalNote.label')}
                placeholder={t('propertyVisits.addEditView.pageTexts.form.internalNote.placeholder')}
                value={formData.internalNote}
                rows={4}
                className="form-control-lg h-100"
                disabled={!isEditable}
                {...fieldError(VALIDATION_FIELD_MAPPING.internalNote)}
                onChange={(e: any) => onInputChange(e, VALIDATION_FIELD_MAPPING.internalNote)}
                onBlur={(e: any) => onInputBlur(e, VALIDATION_FIELD_MAPPING.internalNote)}
              />
            </div>
          </div>

          {/* Internal Status */}
          <div className="col-md-3">
            <div className="form-floating form-floating-outline">
              <Dropdown
                values={formData.internalStatus ? [formData.internalStatus] : []}
                disabled={!isEditable || !isAdmin || isAddMode}
                options={statusOptions}
                allOptions={statusOptions}
                id={VALIDATION_FIELD_MAPPING.internalStatus}
                name={VALIDATION_FIELD_MAPPING.internalStatus}
                searchText={dropdownSearchData.internalStatus}
                onSearch={onDropdownSearchChange}
                placeholder={t('propertyVisits.addEditView.pageTexts.form.internalStatus.placeholder')}
                label={t('propertyVisits.addEditView.pageTexts.form.internalStatus.label')}
                onChange={onInputChange}
                onBlur={onInputBlur}
              />
            </div>
          </div>
        </div>
      </form>

      {isEditable && (
        <>
          <hr className="m-0" />
          <div className="p-4">
            <Button
              className="btn btn-primary me-3"
              text={isAddMode
                ? t('propertyVisits.addEditView.pageTexts.form.saveVisitBtn')
                : t('propertyVisits.addEditView.pageTexts.form.saveChangesBtn')}
              onClick={onSave}
              loading={loading}
            />
            <Button
              className="btn btn-outline-secondary"
              text={t('propertyVisits.addEditView.pageTexts.form.cancelBtn')}
              onClick={onCancel}
              disabled={loading}
            />
          </div>
        </>
      )}
    </>
  );
};

export default VisitTab;
